<template>
  <div class="summary-container mt-2">
    <h4
      id="title-summary-wise"
      data-testid="title-summary-wise"
      class="title-summary"
    >
      {{ $t('addFundPage.creditCardPayment.textSummary') }}
    </h4>
    <div class="d-flex align-items-center justify-content-between mb-1">
      <div
        id="title-amount-wise"
        data-testid="title-amount-wise"
        class="font-14 text-placeholder"
      >
        {{ $t('balance.textAmount') }}
      </div>
      <strong
        v-if="amountValidate"
        id="current-amount-wise"
        data-testid="current-amount-wise"
      >
        {{ localStringFormat(amount) }} USD
      </strong>
      <strong v-else> -- </strong>
    </div>

    <div
      class="d-flex align-items-center justify-content-between mb-1"
    >
      <div
        id="title-wise-method"
        data-testid="title-wise-method"
        class="font-14 text-placeholder"
      >
        {{ $t('addFundPage.creditCardPayment.textPaymentMethod') }}
      </div>
      <div
        v-if="amount"
        id="wise-method-field"
        data-testid="wise-method-field"
        class="d-flex align-items-center"
      >
        <img
          :src="getBrandIcon(PAYMENT_METHOD.TRANSFERWISE)"
          alt="payment"
          style="margin-right: 6px;width: 18px"
        >
        <strong id="payment-method">{{ convertNamePayment(PAYMENT_METHOD.TRANSFERWISE) }}</strong>
      </div>
      <strong v-else> -- </strong>
    </div>

    <div
      v-if="percentFee"
      class="d-flex align-items-center justify-content-between mb-1"
    >
      <div
        id="title-service-fee-wise"
        data-testid="title-service-fee-wise"
        class="font-14 text-placeholder"
      >
        {{ $t('payment.balanceTransactionsTable.textServiceCharge') }}
        <small
          id="percent-service-charge-wise-fee"
          data-testid="percent-service-charge-wise-fee"
          class="font-small-1"
          style="margin-right: 6px"
        >
          {{ `(${percentFeeTxt})` }}
        </small>
      </div>
      <div>
        <div
          v-if="amountValidate"
          id="wise-service-charge-fee"
          data-testid="wise-service-charge-fee"
          class="d-flex align-items-center"
        >
          <strong
            id="current-wise-service-charge-fee"
            data-testid="current-wise-service-charge-fee"
            class="text-primary"
          >{{ serviceCharge }} USD</strong>
        </div>
        <strong v-else> -- </strong>
      </div>
    </div>

    <div
      v-if="methodFee"
      class="d-flex align-items-center justify-content-between mb-1"
    >
      <div
        id="title-wise-fee"
        data-testid="title-wise-fee"
        class="font-14 text-placeholder"
      >
        {{ $t('payment.paypal.textPaymentFee', {payment:"Wise"}) }}
        <small
          id="percent-wise-fee"
          data-testid="percent-wise-fee"
          class="font-small-1"
          style="margin-right: 6px"
        >
          {{ `(${methodFeeTxt})` }}
        </small>
      </div>
      <div>
        <div
          v-if="amountValidate"
          class="d-flex align-items-center"
        >
          <strong
            id="current-wise-fee"
            data-testid="current-wise-fee"
            class="text-primary"
          >{{ wiseFee }} USD</strong>
        </div>
        <strong v-else>
          --
        </strong>
      </div>
    </div>

    <div
      v-if="taxRate"
      class="d-flex align-items-center justify-content-between mb-1"
    >
      <div
        id="title-tax-fee-wise"
        data-testid="title-tax-fee-wise"
        class="font-14 text-placeholder"
      >
        {{ $t('payment.balanceTransactionsTable.textTax') }}
        <small
          id="percent-tax-wise-fee"
          data-testid="percent-tax-wise-fee"
          class="font-small-1"
          style="margin-right: 6px"
        >
          {{ `(${percentTaxTxt})` }}
        </small>
      </div>
      <div>
        <div
          v-if="amountValidate"
          id="wise-tax-fee"
          data-testid="wise-tax-fee"
          class="d-flex align-items-center"
        >
          <strong
            id="current-wise-tax-fee"
            data-testid="current-wise-tax-fee"
            class="text-primary"
          >{{ tax }} USD</strong>
        </div>
        <strong v-else> -- </strong>
      </div>
    </div>

    <div
      v-if="hasAMId && tipMode"
      class="d-flex align-items-center justify-content-between"
    >
      <div
        id="title-tip-wise"
        data-testid="title-tip-wise"
        class="font-14 text-placeholder"
      >
        Tip
        <small
          v-if="currentTip > 0"
          id="percent-tip-wise-fee"
          data-testid="percent-tip-wise-fee"
          class="font-small-1"
          style="margin-right: 6px"
        >
          {{ `(${percentTipTxt})` }}
        </small>
      </div>
      <div>
        <div
          v-if="amountValidate"
          id="wise-tip-amount"
          data-testid="wise-tip-amount"
          class="d-flex align-items-center"
        >
          <strong
            id="current-wise-tip-amount"
            data-testid="current-wise-tip-amount"
            class="text-primary"
          >{{ amountTipTxt }} USD</strong>
        </div>
        <strong v-else>
          --
        </strong>
      </div>
    </div>

    <hr class="hr-custom">
    <div class="d-flex align-items-center justify-content-between pb-3">
      <div
        id="title-total-amount-wise"
        data-testid="title-total-amount-wise"
        class="font-24 text-placeholder"
      >
        {{ $t('addFundPage.creditCardPayment.textTotalAmount') }}
      </div>
      <strong
        v-if="amountValidate"
        id="current-wise-total-amount"
        data-testid="current-wise-total-amount"
        class="font-24 total-amount"
      >
        {{ totalAmount }} USD
      </strong>
      <strong v-else> -- </strong>
    </div>
  </div>
</template>

<script>
import paymentMixin from '@/mixins/paymentMixin'
import generalConfigsMixin from '@/mixins/generalConfigsMixin'
import numberFormatMixin from '@/mixins/numberFormatMixin'

// plugins
import { createNamespacedHelpers } from 'vuex'
import envMixin from '@/mixins/envMixin'
import { PAYMENT_METHOD } from '@/constants'

const { mapGetters: mapGettersAuth } = createNamespacedHelpers('auth')

export default {
  mixins: [paymentMixin, generalConfigsMixin, numberFormatMixin, envMixin],

  props: {
    amount: {
      type: Number,
      required: true,
    },
    paymentMethod: {
      type: Object,
      required: true,
    },
    transfer: {
      type: Object,
      required: true,
    },
    amountTip: {
      type: Number,
      required: true,
    },
    currentTip: {
      type: Number,
      required: true,
    },
  },

  computed: {
    PAYMENT_METHOD() {
      return PAYMENT_METHOD
    },
    ...mapGettersAuth(['user']),

    isInTrial() {
      return this.user?.isInTrial
    },

    percentFee() {
      return this.user?.data?.plans?.transferFee?.transferWisePaymentLink || 0
    },

    percentFeeTxt() {
      return `${(this.percentFee * 100).toFixed(2)}%`
    },

    percentTaxTxt() {
      return `${(this.taxRate * 100).toFixed(2)}%`
    },

    methodFee() {
      return this.generalConfigs?.paymentGateFee?.transferWisePaymentLinkGate || 0
    },

    methodFeeTxt() {
      return `${(this.methodFee * 100).toFixed(2)}%`
    },

    wiseFee() {
      return this.amountFormat(this.methodFee * this.amount).toLocaleString('en-US')
    },

    tax() {
      // return parseFloat(this.truncDigits(this.taxRate * this.amount)).toLocaleString('en-US')
      return this.amountFormat(this.taxRate * this.amount).toLocaleString(
        'en-US',
      )
    },

    percentTipTxt() {
      return `${(this.currentTip).toFixed(2)}%`
    },

    amountTipTxt() {
      return this.amountFormat(this.amountTip).toLocaleString('en-US')
    },

    totalAmount() {
      const fee = this.amountFormat(this.percentFee * this.amount)
      const tax = this.amountFormat(this.taxRate * this.amount)
      const methodFee = this.amountFormat(this.methodFee * this.amount)
      // return parseFloat(this.truncDigits(this.amount + fee + tax)).toLocaleString('en-US')
      return (this.amount + fee + tax + methodFee + this.amountTip).toLocaleString('en-US')
    },

    serviceCharge() {
      // return parseFloat(this.truncDigits(this.percentFee * this.amount)).toLocaleString('en-US')
      return this.amountFormat(this.percentFee * this.amount).toLocaleString(
        'en-US',
      )
    },

    amountValidate() {
      return !!(this.amount >= this.minTopUp && this.amount <= this.maxAmount)
    },

    maxAmount() {
      return 100000
    },
  },
}
</script>

<style lang="scss" scoped>
.summary-container {
  padding: 20px;
  background-color: #F5F5F7;
  // border: 1px solid rgba(22, 33, 62, 0.1);
  border-radius: 16px;

  .title-summary{
    margin-bottom: 21px;
  }
}

.transfer {
  max-width: 125px;
}

.total-amount{
  color: #00B9FF;
}
</style>
